/**
 * The booth station, the admin station: the stations, who's here now, the shelter roll, visitors (split out of BoothStation.tsx
 * by interaction — James, Oct 21, item 5; no behavior change).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { formatTime } from '@cider/ui';
import { api, isRefused, sentence } from '../../shell/api.js';
import { hours, late, stat } from './BoothShared.js';

export interface AdminView {
  day: string;
  practice: boolean;
  asOf: string;
  strip: {
    expected: number;
    checkedIn: number;
    late: number;
    noShows: number;
    walkUps: number;
    unsigned: number | null;
    onSite: number;
    minorsOnSite: number;
  };
  byHour: { at: string; expected: number; in: number; noShows: number; walkUps: number }[];
  late: { personId: string; name: string; area: string; startsAt: string; minutes: number }[];
  unsigned:
    | {
        personId: string;
        name: string;
        area: string;
        startsAt: string;
        /** A minor: the parent named on the family. */
        parent?: { personId: string; name: string } | null;
      }[]
    | null;
  noShowList?: {
    personId: string;
    name: string;
    area: string;
    startsAt: string;
    endsAt: string;
  }[];
  minorsHere?: { personId: string; name: string; age: number | null; area: string; inAt: string }[];
  short: {
    shiftId: string;
    area: string;
    title: string;
    startsAt: string;
    endsAt: string;
    in: number;
    capacity: number;
  }[];
  standbys: { id: string; name: string; since: string }[];
  stations: { label: string; operator: string | null; ok: boolean; says: string }[];
  tomorrow: {
    day: string;
    expected: number;
    rush: { at: string; n: number } | null;
    unsigned: number | null;
    minors: number;
    groups: { name: string; n: number }[];
    byHour: { at: string; n: number }[];
  } | null;
  says: string;
}

/**
 * **The admin station** (ON11 item 6; the drawing's `admin` and `tomorrow`): the booth chair's
 * day — the strip, today by hour, late, unsigned, short now with the standbys, the stations, and
 * tomorrow. It reads once when opened and again on Read again; it never polls.
 */
/**
 * The admin view, read once per page — the admin station, Lists and Tomorrow share one door: the
 * booth chair's and the directors' (James, Oct 9). Anyone else gets the sentence and no screen.
 */
export function useAdminView(): { v: AdminView | null; said: string | null; load: () => void } {
  const [v, setV] = useState<AdminView | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(() => {
    api<AdminView>('GET', '/volunteers/booth/admin')
      .then((r) => {
        setV(r);
        setSaid(null);
      })
      .catch((e: unknown) => {
        setSaid(isRefused(e) ? sentence(e) : 'The admin station did not answer. Try again.');
      });
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return { v, said, load };
}

export function AdminStation({
  onOpen,
}: {
  onOpen: (panel: 'lists' | 'tomorrow' | 'whoshere') => void;
}): ReactNode {
  const { v, said, load } = useAdminView();
  return (
    <div className="booth__found" data-testid="booth-admin">
      {said ? <p className="booth__refusal">{said}</p> : null}
      {v ? (
        <>
          <div className="booth__strip">
            {stat(String(v.strip.expected), 'EXPECTED TODAY')}
            {stat(String(v.strip.checkedIn), `CHECKED IN · BY ${formatTime(v.asOf)}`, 'ok')}
            {stat(String(v.strip.late), 'LATE 15+ MIN', v.strip.late ? 'warn' : undefined)}
            {stat(String(v.strip.noShows), 'NO-SHOWS · SHIFTS OVER')}
            {stat(String(v.strip.walkUps), 'WALK-UPS')}
            {stat(
              v.strip.unsigned === null ? '—' : String(v.strip.unsigned),
              'UNSIGNED · TODAY’S SHIFTS',
              v.strip.unsigned ? 'warn' : undefined,
            )}
          </div>
          <p className="booth__fine">
            {v.says} · {String(v.strip.onSite)} on site · {String(v.strip.minorsOnSite)} minors
            {v.practice ? ' · practice day' : ''}
          </p>
          <section className="booth__card booth__card--red">
            <div className="booth__cardhead">
              SHORT RIGHT NOW
              <span className="booth__cardsub">under capacity · who can go</span>
            </div>
            {v.short.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Every shift on now is full.</span>
              </div>
            ) : null}
            {v.short.map((x) => (
              <div className="booth__row" key={x.shiftId}>
                <span className="booth__rowa">
                  <b>
                    {x.area} · {hours(x.startsAt, x.endsAt)}
                  </b>
                  <span className="booth__rowb">
                    {String(x.in)} of {String(x.capacity)} · short {String(x.capacity - x.in)}
                  </span>
                </span>
                <span className="booth__rowc">
                  {v.standbys.length
                    ? `Standby: ${v.standbys.map((sb) => `${sb.name} since ${formatTime(sb.since)}`).join(', ')}.`
                    : ''}
                </span>
                <span className="booth__chip booth__chip--bad">
                  SHORT {String(x.capacity - x.in)}
                </span>
              </div>
            ))}
            <div className="booth__cardfoot">
              {v.standbys.length
                ? 'Send someone on standby from the standby list at any station: it puts them on the shift now and the area chair hears they are on the way.'
                : 'Nobody on standby.'}
            </div>
          </section>
          <section className="booth__card booth__card--red">
            <div className="booth__cardhead">
              LATE BY MORE THAN 15 MINUTES
              <span className="booth__cardsub">{String(v.late.length)} · worst first</span>
            </div>
            {v.late.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Nobody is late.</span>
              </div>
            ) : null}
            {v.late.map((x) => (
              <div className="booth__row" key={`${x.personId}${x.startsAt}`}>
                <span className="booth__rowa">
                  <b>{x.name}</b>
                  <span className="booth__rowb">
                    {x.area} · {formatTime(x.startsAt)} · {late(x.minutes)}
                  </span>
                </span>
                <span className="booth__chip booth__chip--bad">LATE</span>
              </div>
            ))}
          </section>
          <section className="booth__card">
            <div className="booth__cardhead">
              THE STATIONS
              <span className="booth__cardsub">who is at each desk · last sync</span>
            </div>
            {v.stations.map((x) => (
              <div className="booth__row" key={x.label}>
                <span className="booth__rowa">
                  <b>{x.label}</b>
                  <span className="booth__rowb">{x.operator ?? 'nobody at the desk'}</span>
                </span>
                <span className="booth__rowc">{x.says}</span>
                <span className={`booth__chip booth__chip--${x.ok ? 'ok' : 'warn'}`}>
                  {x.ok ? 'OK' : 'CHECK'}
                </span>
              </div>
            ))}
          </section>
          <section className="booth__card">
            <div className="booth__cardhead">
              TODAY BY HOUR
              <span className="booth__cardsub">expected · in · no-shows · walk-ups</span>
            </div>
            {v.byHour.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">No shifts today.</span>
              </div>
            ) : null}
            {v.byHour.map((h) => (
              <div className="booth__row" key={h.at}>
                <span className="booth__rowa">
                  <b>{formatTime(h.at)}</b>
                </span>
                <span className="booth__rowc">
                  {String(h.expected)} · {String(h.in)} · {String(h.noShows)} · {String(h.walkUps)}
                </span>
                {h.expected - h.in - h.noShows > 3 ? (
                  <span className="booth__chip booth__chip--warn">
                    GAP {String(h.expected - h.in - h.noShows)}
                  </span>
                ) : null}
              </div>
            ))}
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                onOpen('lists');
              }}
            >
              Lists
            </button>
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                onOpen('tomorrow');
              }}
            >
              Tomorrow
            </button>
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                onOpen('whoshere');
              }}
            >
              Who’s here now
            </button>
            <VisitorsSwitch />
            <button type="button" className="booth__side-act" onClick={load}>
              Read again
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * **Lists** (`Booth.dc.html` lists, Rebuild 29 state 29): no-shows (a shift over, never arrived),
 * standby (send or release), the unsigned waivers among today's shifts, and the minors on site.
 * A no-show and a minor's parent are texted from here (Oct 9) — by first name, never a pronoun.
 */
/**
 * **Who's here now** (ADR 0032; Booth.dc.html `whoshere`): two kinds of here, and only two words —
 * ON SITE (role holders, from I'm here) and ON SHIFT (volunteers checked in at the booth and not
 * yet out). Never "on the grounds", never an evacuation count. The admin station's own door.
 */
export interface WhosHere {
  asOf: string;
  onSite: {
    personId: string;
    name: string;
    role: string;
    since: string;
    place: string | null;
    kit: string[];
    visitorId: string | null;
  }[];
  onShift: { personId: string; name: string; area: string; since: string }[];
  radiosOut: number | null;
}

export function WhosHereNow({ onShelter }: { onShelter: () => void }): ReactNode {
  const [v, setV] = useState<WhosHere | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(() => {
    api<WhosHere>('GET', '/volunteers/booth/whos-here')
      .then((r) => {
        setV(r);
        setSaid(null);
      })
      .catch((e: unknown) => {
        setSaid(isRefused(e) ? sentence(e) : 'Who’s here now did not answer. Try again.');
      });
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="booth__found" data-testid="booth-whos-here">
      {said ? <p className="booth__refusal">{said}</p> : null}
      {v ? (
        <>
          <div className="booth__banner">
            <b>ON SITE AND ON SHIFT ARE THE ONLY TWO WORDS FOR HERE</b>
            This list is the people who said I’m here, and the volunteers checked in. The public is
            the plan’s sweep, not this screen.
          </div>
          <div className="booth__strip">
            {stat(String(v.onSite.length), 'ON SITE · ROLE HOLDERS AND VISITORS')}
            {stat(String(v.onShift.length), 'ON SHIFT · FROM BOOTH CHECK-INS', 'ok')}
            {stat(v.radiosOut === null ? '—' : String(v.radiosOut), 'RADIOS OUT · THE KIT LOG')}
          </div>
          <section className="booth__card">
            <div className="booth__cardhead">
              ON SITE
              <span className="booth__cardsub">
                role holders by role, then name · from I’m here · a radio if one is out to them
              </span>
            </div>
            {v.onSite.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Nobody has said I’m here yet.</span>
              </div>
            ) : null}
            {v.onSite.map((x) => (
              <div className="booth__row" key={x.personId}>
                <span className="booth__rowa">
                  <b>
                    {x.name} · {x.role}
                  </b>
                  <span className="booth__rowb">
                    here since {formatTime(x.since)}
                    {x.place ? ` · ${x.place}` : ''}
                  </span>
                </span>
                {x.kit.map((k) => (
                  <span className="booth__chip booth__chip--warn" key={k}>
                    {k}
                  </span>
                ))}
                {x.visitorId ? (
                  <>
                    <span className="booth__chip">VISITOR</span>
                    <button
                      type="button"
                      className="booth__act"
                      onClick={() => {
                        api<{ says: string }>(
                          'POST',
                          `/volunteers/booth/visitors/${x.visitorId ?? ''}/out`,
                        )
                          .then((r) => {
                            setSaid(r.says);
                            load();
                          })
                          .catch((e: unknown) => {
                            setSaid(isRefused(e) ? sentence(e) : 'That did not go through.');
                          });
                      }}
                    >
                      Check out
                    </button>
                  </>
                ) : null}
              </div>
            ))}
          </section>
          <section className="booth__card booth__card--ok">
            <div className="booth__cardhead">
              ON SHIFT
              <span className="booth__cardsub">
                volunteers by area · time in · checked in at the booth today, not yet out
              </span>
            </div>
            {v.onShift.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Nobody is checked in right now.</span>
              </div>
            ) : null}
            {[...new Set(v.onShift.map((x) => x.area))].map((area) => {
              const here = v.onShift.filter((x) => x.area === area);
              const earliest = [...here].sort((a, b) => a.since.localeCompare(b.since))[0];
              return (
                <div className="booth__row" key={area}>
                  <span className="booth__rowa">
                    <b>
                      {area || 'An area'} · {String(here.length)}
                    </b>
                    <span className="booth__rowb">{here.map((x) => x.name).join(' · ')}</span>
                  </span>
                  {earliest ? (
                    <span className="booth__rowc">earliest in {formatTime(earliest.since)}</span>
                  ) : null}
                  <span className="booth__chip booth__chip--info">{String(here.length)}</span>
                </div>
              );
            })}
            {v.onShift.length ? (
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>Total on shift</b>
                </span>
                <span className="booth__chip booth__chip--ok">{String(v.onShift.length)}</span>
              </div>
            ) : null}
          </section>
          <div className="booth__acts">
            <button
              type="button"
              className="booth__side-act booth__side-act--hot"
              onClick={onShelter}
            >
              Shelter roll
            </button>
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                window.print();
              }}
            >
              Print the paper list
            </button>
            <button type="button" className="booth__side-act" onClick={load}>
              Read again
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * **The shelter roll** (ADR 0032; Booth.dc.html `shelter`): during a hold, the same two lists with a
 * tick per person by the person at the desk, the un-ticked first. Never an evacuation count. The
 * roll is kept when the hold ends.
 */
export interface Shelter {
  hold: { id: string; calledAt: string; calledBy: string } | null;
  notYet: { personId: string; name: string; list: 'site' | 'shift'; line: string }[];
  accounted: { personId: string; name: string; list: 'site' | 'shift'; by: string; at: string }[];
}

export function ShelterRoll(): ReactNode {
  const [v, setV] = useState<Shelter | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api<Shelter>('GET', '/volunteers/booth/shelter')
      .then((r) => {
        setV(r);
      })
      .catch((e: unknown) => {
        setSaid(isRefused(e) ? sentence(e) : 'The shelter roll did not answer. Try again.');
      });
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const post = (path: string, data?: Record<string, unknown>): void => {
    setBusy(true);
    api<{ says: string }>('POST', path, data)
      .then((r) => {
        setSaid(r.says);
        load();
      })
      .catch((e: unknown) => {
        setSaid(isRefused(e) ? sentence(e) : 'That did not go through. Try again.');
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const done = (list: 'site' | 'shift'): number =>
    (v?.accounted ?? []).filter((x) => x.list === list).length;
  const all = (list: 'site' | 'shift'): number =>
    done(list) + (v?.notYet ?? []).filter((x) => x.list === list).length;
  return (
    <div className="booth__found" data-testid="booth-shelter">
      {said ? (
        <p className="booth__fine" role="status">
          {said}
        </p>
      ) : null}
      {v && !v.hold ? (
        <>
          <p className="booth__fine">
            No hold is on. The roll opens in the name of the person at the desk, with the same two
            lists as Who’s here now.
          </p>
          <div className="booth__acts">
            <button
              type="button"
              className="booth__side-act booth__side-act--hot"
              disabled={busy}
              onClick={() => {
                post('/volunteers/booth/shelter/start');
              }}
            >
              Start the shelter roll
            </button>
          </div>
        </>
      ) : null}
      {v?.hold ? (
        <>
          <div className="booth__banner booth__banner--warn">
            <b>
              HOLD CALLED {formatTime(v.hold.calledAt)} BY {v.hold.calledBy.toUpperCase()} · THE
              SAME TWO LISTS, A TICK PER PERSON
            </b>
            Our own people only. The public is the plan’s sweep. Chairs tick their own people from
            their phones; the un-ticked rise to the top.
          </div>
          <div className="booth__strip">
            {stat(String(all('site')), `ON SITE · ${String(done('site'))} ACCOUNTED FOR`)}
            {stat(String(all('shift')), `ON SHIFT · ${String(done('shift'))} ACCOUNTED FOR`, 'ok')}
            {stat(
              String(v.notYet.length),
              'NOT YET · SORTED TO THE TOP',
              v.notYet.length ? 'warn' : undefined,
            )}
          </div>
          <section className="booth__card booth__card--red">
            <div className="booth__cardhead">
              NOT YET · {String(v.notYet.length)}
              <span className="booth__cardsub">un-ticked first</span>
            </div>
            {v.notYet.length === 0 ? (
              <div className="booth__row">
                <span className="booth__rowc">Everybody on both lists is accounted for.</span>
              </div>
            ) : null}
            {v.notYet.map((x) => (
              <div className="booth__row" key={x.personId}>
                <span className="booth__rowa">
                  <b>{x.name}</b>
                  <span className="booth__rowb">{x.line}</span>
                </span>
                <span className="booth__chip booth__chip--bad">NOT YET</span>
                <button
                  type="button"
                  className="booth__act"
                  disabled={busy}
                  onClick={() => {
                    post('/volunteers/booth/shelter/tick', { personId: x.personId, list: x.list });
                  }}
                >
                  Accounted for
                </button>
              </div>
            ))}
          </section>
          <section className="booth__card booth__card--ok">
            <div className="booth__cardhead">
              ACCOUNTED FOR · {String(v.accounted.length)}
              <span className="booth__cardsub">who · by whom · when</span>
            </div>
            {v.accounted.map((x) => (
              <div className="booth__row" key={x.personId}>
                <span className="booth__rowa">
                  <b>{x.name}</b>
                  <span className="booth__rowb">
                    accounted for · by {x.by} · {formatTime(x.at)}
                  </span>
                </span>
                <span className="booth__chip booth__chip--ok">TICKED</span>
              </div>
            ))}
          </section>
          <div className="booth__acts">
            <button
              type="button"
              className="booth__side-act booth__side-act--navy"
              disabled={busy}
              onClick={() => {
                post('/volunteers/booth/shelter/end');
              }}
            >
              End the hold · the roll is kept
            </button>
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                window.print();
              }}
            >
              Print the roll
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * **The visitor kind's switch** (ADR 0032): the booth chair turns visitors off on a day the booth
 * is slammed, and on again. Behind the admin station's door.
 */
export function VisitorsSwitch(): ReactNode {
  const [on, setOn] = useState<boolean | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    api<{ on: boolean }>('GET', '/volunteers/booth/visitors/setting')
      .then((r) => {
        setOn(r.on);
      })
      .catch(() => {
        setOn(null);
      });
  }, []);
  if (on === null) return null;
  return (
    <>
      <button
        type="button"
        className="booth__side-act"
        onClick={() => {
          api<{ says: string }>('POST', '/volunteers/booth/visitors/setting', { on: !on })
            .then((r) => {
              setOn(!on);
              setSaid(r.says);
            })
            .catch((e: unknown) => {
              setSaid(isRefused(e) ? sentence(e) : 'That did not go through.');
            });
        }}
      >
        {on ? 'Turn visitors off' : 'Turn visitors on'}
      </button>
      {said ? (
        <p className="booth__fine" role="status">
          {said}
        </p>
      ) : null}
    </>
  );
}

/**
 * **A visitor — not a volunteer** (ADR 0032; Booth.dc.html `visitor`): ride staff, the band, a
 * driver, a guest, an inspector. The shorter card — who, here for — and the visitor waiver on the
 * guest screen. On site, never on shift; never earns hours. Shown only while visitors are on.
 */
export function VisitorEntry({
  onTag,
  onOpen,
}: {
  /** Prints the visitor's tag through the station's own tag. */
  onTag: (name: string, hereFor: string) => void;
  /** The visitor card takes the walk-up's place while it is open. */
  onOpen: (open: boolean) => void;
}): ReactNode {
  const [on, setOn] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [hereFor, setHereFor] = useState('');
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<{ on: boolean }>('GET', '/volunteers/booth/visitors/setting')
      .then((r) => {
        setOn(r.on);
      })
      .catch(() => {
        setOn(false);
      });
  }, []);
  if (!on) return null;
  if (!open)
    return (
      <button
        type="button"
        className="booth__link"
        onClick={() => {
          setOpen(true);
          onOpen(true);
        }}
      >
        A visitor · not a volunteer
      </button>
    );
  const first = name.trim().split(' ')[0] ?? '';
  return (
    <form
      className="booth__found"
      data-testid="booth-visitor"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        api<{ id: string; says: string }>('POST', '/volunteers/booth/visitors', {
          name,
          phone: phone || null,
          company: company || null,
          hereFor,
        })
          .then(async (r) => {
            await api('POST', '/volunteers/booth/guest/show', {
              state: 'visitor',
              visitorId: r.id,
            });
            setSaid(`${r.says} The visitor waiver is on the guest screen; the tag prints.`);
            onTag(name.trim(), hereFor.trim());
            setName('');
            setPhone('');
            setCompany('');
            setHereFor('');
          })
          .catch((e: unknown) => {
            setSaid(isRefused(e) ? sentence(e) : 'That did not go through. Try again.');
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <section className="booth__card">
        <div className="booth__cardhead">
          WHO
          <span className="booth__cardsub">the shorter card</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Name</b>
          </span>
          <label className="booth__rowc">
            <span className="booth__k">NAME</span>
            <input
              className="booth__input"
              value={name}
              required
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </label>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Phone</b>
          </span>
          <label className="booth__rowc">
            <span className="booth__k">PHONE</span>
            <input
              className="booth__input"
              inputMode="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
              }}
            />
          </label>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Company</b>
          </span>
          <label className="booth__rowc">
            <span className="booth__k">COMPANY · OR WHO THEY’RE WITH</span>
            <input
              className="booth__input"
              value={company}
              onChange={(e) => {
                setCompany(e.target.value);
              }}
            />
          </label>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Here for</b>
          </span>
          <label className="booth__rowc">
            <span className="booth__k">HERE FOR</span>
            <input
              className="booth__input"
              value={hereFor}
              required
              placeholder="an area or a person"
              onChange={(e) => {
                setHereFor(e.target.value);
              }}
            />
          </label>
        </div>
        <div className="booth__cardfoot">
          A visitor is on site, not on shift: under ON SITE in Who’s here now, never earns hours,
          never enters a ledger. The visitor waiver is DRAFT until the festival gives its words.
        </div>
      </section>
      {said ? (
        <p className="booth__fine" role="status">
          {said}
        </p>
      ) : null}
      <button
        type="submit"
        className="booth__big"
        disabled={busy || !name.trim() || !hereFor.trim()}
      >
        {first ? `Check in ${first} · visitor` : 'Check in · visitor'}
      </button>
    </form>
  );
}
